import {
  useTranslation
} from "../../../../chunk-6N4NEKWU.mjs";
import "../../../../chunk-D57E6H3M.mjs";

// src/components/composite/Timeline/OrderStatusCard/OrderStatusCard.tsx
import { useId } from "react";
import { Badge } from "../../../primitive/Badge/Badge.mjs";
import { Link } from "../../../primitive/Link/Link.mjs";
import { Text } from "../../../primitive/Text/Text.mjs";
import { orderStatusKey, shipmentDate, shipmentProgress, shipmentStatusKey } from "./order-status.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
function formatDate(value, locale, utc = false) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = /* @__PURE__ */ new Date();
  const sameYear = utc ? date.getUTCFullYear() === now.getUTCFullYear() : date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", ...!sameYear && { year: "numeric" }, ...utc && { timeZone: "UTC" } }).format(date);
}
function trackingName(t, tracking) {
  const { carrier, number } = tracking;
  if (carrier && number) return t("orderStatus.trackAria", { carrier, number });
  if (number) return t("orderStatus.trackAriaNoCarrier", { number });
  return t("orderStatus.trackAriaBare");
}
function TrackingRow({ tracking }) {
  const { t } = useTranslation();
  const label = tracking.number ?? t("orderStatus.track");
  return /* @__PURE__ */ jsxs("li", { className: "buyscout-order-status-tracking", children: [
    tracking.carrier && /* @__PURE__ */ jsx("span", { className: "buyscout-order-status-carrier", children: tracking.carrier }),
    tracking.url ? /* @__PURE__ */ jsx(Link, { external: true, href: tracking.url, "aria-label": trackingName(t, tracking), className: "buyscout-order-status-tracking-link", children: label }) : tracking.number && /* @__PURE__ */ jsx("span", { className: "buyscout-order-status-tracking-number", children: tracking.number })
  ] });
}
function ShipmentRow({ shipment, index, count }) {
  const { t, i18n } = useTranslation();
  const progress = shipmentProgress(shipment.status);
  const date = shipmentDate(shipment);
  return /* @__PURE__ */ jsxs("li", { className: "buyscout-order-status-shipment", children: [
    count > 1 && /* @__PURE__ */ jsx(Text, { variant: "caption", as: "p", className: "buyscout-order-status-shipment-label", children: t("orderStatus.shipmentOf", { index: index + 1, count }) }),
    /* @__PURE__ */ jsxs("div", { className: "buyscout-order-status-shipment-head", children: [
      /* @__PURE__ */ jsx(Text, { variant: "body-bold", as: "p", className: "buyscout-order-status-shipment-status", children: t(shipmentStatusKey(shipment.status)) }),
      date && /* @__PURE__ */ jsx(Text, { variant: "body-sm", as: "p", className: "buyscout-order-status-shipment-date", children: date.kind === "delivered" ? t("orderStatus.deliveredOn", { date: formatDate(date.at, i18n.resolvedLanguage) }) : t("orderStatus.expectedOn", { date: formatDate(date.at, i18n.resolvedLanguage, true) }) })
    ] }),
    progress && /* @__PURE__ */ jsx("ol", { className: "buyscout-order-status-track", "aria-label": t("orderStatus.progressLabel"), "data-track": progress.track, children: progress.steps.map((step, stepIndex) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "buyscout-order-status-step",
        "data-state": stepIndex < progress.reached ? "complete" : stepIndex === progress.reached ? "current" : "upcoming",
        "aria-current": stepIndex === progress.reached ? "step" : void 0,
        children: [
          /* @__PURE__ */ jsx("span", { className: "buyscout-order-status-step-dot", "aria-hidden": true }),
          /* @__PURE__ */ jsx("span", { className: "buyscout-order-status-step-label", children: t(`orderStatus.step.${step}`) })
        ]
      },
      step
    )) }),
    shipment.tracking.length > 0 && // eslint-disable-next-line jsx-a11y/no-redundant-roles -- WebKit drops list semantics from a list styled without markers unless the role is explicit.
    /* @__PURE__ */ jsx("ul", { role: "list", className: "buyscout-order-status-tracking-list", children: shipment.tracking.map((entry, entryIndex) => /* @__PURE__ */ jsx(TrackingRow, { tracking: entry }, `${entry.number ?? ""}-${entryIndex}`)) })
  ] });
}
function OrderStatusCard({ ref, order, className, ...props }) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const placed = formatDate(order.placedAt, i18n.resolvedLanguage);
  return /* @__PURE__ */ jsxs("article", { ref, "aria-labelledby": headingId, className: `buyscout-order-status-card ${className ?? ""}`.trim(), ...props, children: [
    /* @__PURE__ */ jsxs("header", { className: "buyscout-order-status-header", children: [
      /* @__PURE__ */ jsxs("div", { className: "buyscout-order-status-title", children: [
        /* @__PURE__ */ jsx(Text, { variant: "body-bold", as: "h3", id: headingId, className: "buyscout-order-status-number", children: t("orderStatus.heading", { number: order.orderNumber }) }),
        placed && /* @__PURE__ */ jsx(Text, { variant: "body-sm", as: "p", className: "buyscout-order-status-placed", children: t("orderStatus.placedOn", { date: placed }) })
      ] }),
      /* @__PURE__ */ jsx(Badge, { variant: "solid", color: "primary", size: "sm", className: "buyscout-order-status-badge", children: t(orderStatusKey(order.status)) })
    ] }),
    order.shipments.length > 0 && // eslint-disable-next-line jsx-a11y/no-redundant-roles -- WebKit drops list semantics from a list styled without markers unless the role is explicit.
    /* @__PURE__ */ jsx("ul", { role: "list", className: "buyscout-order-status-shipments", children: order.shipments.map((shipment, index) => (
      // Shipments carry no ID; their order is Shopify's fulfillment order and never reshuffles.
      /* @__PURE__ */ jsx(ShipmentRow, { shipment, index, count: order.shipments.length }, index)
    )) })
  ] });
}
OrderStatusCard.displayName = "OrderStatusCard";
export {
  OrderStatusCard
};
//# sourceMappingURL=OrderStatusCard.mjs.map